import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import {
  getIdToken,
  parseActionCodeURL,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, redirect } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { toast } from 'sonner';

import { Toaster } from '@cold-ambulance/shadcn';
import { client, createMutationErrorHandler } from '@cold-ambulance/ui';

import { auth, getIdentity } from './app/firebase';
import Layout, { layoutLoader } from './app/layout.tsx';
import AdditionalDocuments from './app/routes/additional-documents.tsx';
import ForgotPassword from './app/routes/forgot-password/forgot-password.tsx';
import ResetPasswordEmailSent from './app/routes/forgot-password/reset-password-email-sent.tsx';
import Home from './app/routes/home.tsx';
import ProfilePage from './app/routes/profile.tsx';
import AcceptOfferPage from './app/routes/requests/accept-offer.tsx';
import Request, {
  requestEditorLoader,
} from './app/routes/requests/new-request.tsx';
import OfferAccepted from './app/routes/requests/offer-accepted.tsx';
import OfferContractPage from './app/routes/requests/offer-contract.tsx';
import RequestOffersPage from './app/routes/requests/offers.tsx';
import RequestCreated from './app/routes/requests/request-created.tsx';
import { RequestDetailsPage } from './app/routes/requests/request-details.tsx';
import RequestsPage from './app/routes/requests/requests.tsx';
import PasswordReset from './app/routes/reset-password/password-reset.tsx';
import ResetPassword, {
  ResetPasswordError,
} from './app/routes/reset-password/reset-password.tsx';
import SignIn from './app/routes/signin.tsx';
import { Signup, signupLoader } from './app/routes/signup/stepper.tsx';
import i18n from './i18n/i18n';

const router = createBrowserRouter([
  {
    Component: Layout,
    loader: layoutLoader,
    shouldRevalidate: ({ currentUrl, nextUrl, defaultShouldRevalidate }) =>
      currentUrl.pathname === nextUrl.pathname
        ? false
        : defaultShouldRevalidate,
    children: [
      {
        index: true,
        Component: Home,
      },
      {
        path: '/profile',
        Component: ProfilePage,
      },
      {
        path: 'additional-documents',
        Component: AdditionalDocuments,
      },
      {
        path: 'requests',
        children: [
          {
            index: true,
            Component: RequestsPage,
          },
          {
            path: ':id',
            Component: RequestDetailsPage,
          },
          {
            path: ':id/offers',
            Component: RequestOffersPage,
          },
          {
            path: ':requestId/offers/:offerId/accept',
            Component: AcceptOfferPage,
          },
          {
            path: ':requestId/offers/:offerId/contract',
            Component: OfferContractPage,
          },
          {
            path: 'offer-accepted',
            Component: OfferAccepted,
          },
          {
            path: 'new',
            Component: Request,
            loader: requestEditorLoader,
          },
          {
            path: 'drafts/:draftId',
            Component: Request,
            loader: requestEditorLoader,
          },
          {
            path: 'success',
            Component: RequestCreated,
          },
        ],
      },
    ],
  },
  {
    path: '/signin',
    Component: SignIn,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        return null;
      }
      throw redirect('/');
    },
  },
  {
    path: '/signup',
    Component: Signup,
    loader: signupLoader,
  },
  {
    path: '/forgot-password',
    Component: ForgotPassword,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        return null;
      }
      throw redirect('/');
    },
  },
  {
    path: '/reset-password-email-sent',
    Component: ResetPasswordEmailSent,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        return null;
      }
      throw redirect('/');
    },
  },
  {
    path: '/reset-password',
    Component: ResetPassword,
    ErrorBoundary: ResetPasswordError,
    loader: async ({ request }) => {
      const actionCodeURL = parseActionCodeURL(request.url);
      if (!actionCodeURL) {
        throw new Error(i18n.t('errors.resetLinkNotFound'));
      }
      if (actionCodeURL.operation !== 'PASSWORD_RESET') {
        throw new Error(i18n.t('errors.resetLinkNotFound'));
      }
      if (!actionCodeURL.code) {
        throw new Error(i18n.t('errors.verificationCodeNotFound'));
      }

      try {
        const email = await verifyPasswordResetCode(auth, actionCodeURL.code);
        return { email, oobCode: actionCodeURL.code };
      } catch (error) {
        console.error(error);
        throw new Error(i18n.t('errors.verificationCodeInvalid'));
      }
    },
  },
  {
    path: '/password-reset',
    Component: PasswordReset,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        return null;
      }
      throw redirect('/');
    },
  },
]);
const root = createRoot(document.getElementById('root') as HTMLElement);

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: createMutationErrorHandler({
      i18n,
      fallbackMessage: 'حدث خطأ غير متوقع',
      onUnauthorized: () => {
        toast.error('You are not authorized to perform this action');
        window.location.href = '/';
      },
    }),
  }),
  queryCache: new QueryCache({
    onError: (error) => {
      console.error(error);
    },
  }),
});

// Set the token for API client
client.setOptions({
  token: () => {
    if (auth.currentUser) {
      return getIdToken(auth.currentUser);
    }
    return '';
  },
});

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
